/**
 * Card components with theme support (light/dark)
 * Uses Tailwind's dark: prefix for automatic theme switching
 */

import { forwardRef } from 'react'

/**
 * Base card component
 */
export const Card = forwardRef(function Card(
  {
    children,
    className = '',
    padding = 'md',
    hover = false,
    onClick,
    as: Component = 'div',
    variant = 'default',
    ...props
  },
  ref
) {
  const paddingClasses = {
    none: '',
    sm: 'p-3',
    md: 'p-4',
    lg: 'p-6',
  }

  const variantClasses = {
    default: 'bg-white dark:bg-dial-surface border border-gray-200 dark:border-dial-border',
    elevated: 'bg-white dark:bg-dial-elevated border border-gray-200 dark:border-dial-border shadow-md dark:shadow-elevated',
    ghost: 'bg-transparent border border-gray-200 dark:border-dial-border',
  }

  const baseClasses = `rounded-xl ${variantClasses[variant]}`
  const hoverClasses = hover ? 'hover:bg-gray-50 dark:hover:bg-dial-elevated hover:border-gray-300 dark:hover:border-indices-muted transition-all cursor-pointer' : ''
  const clickableClasses = onClick ? 'cursor-pointer' : ''

  return (
    <Component
      ref={ref}
      className={`${baseClasses} ${paddingClasses[padding]} ${hoverClasses} ${clickableClasses} ${className}`}
      onClick={onClick}
      {...props}
    >
      {children}
    </Component>
  )
})

/**
 * Card header component
 */
export function CardHeader({ children, className = '', action }) {
  return (
    <div className={`flex items-center justify-between pb-3 border-b border-gray-200 dark:border-dial-border ${className}`}>
      <div className="font-semibold text-gray-900 dark:text-indices-bright font-display">{children}</div>
      {action && <div>{action}</div>}
    </div>
  )
}

/**
 * Card body component
 */
export function CardBody({ children, className = '' }) {
  return <div className={`py-3 ${className}`}>{children}</div>
}

/**
 * Card footer component
 */
export function CardFooter({ children, className = '' }) {
  return (
    <div className={`pt-3 border-t border-gray-200 dark:border-dial-border ${className}`}>{children}</div>
  )
}

/**
 * Stats card - for displaying metrics (like watch complications)
 */
export function StatsCard({
  icon,
  iconBg = 'bg-primary-100 dark:bg-lume-teal/20',
  iconColor = 'text-primary-600 dark:text-lume-teal',
  label,
  value,
  subValue,
  trend,
  className = '',
}) {
  return (
    <Card className={className}>
      <div className="flex items-center gap-3">
        {icon && (
          <div className={`w-10 h-10 ${iconBg} ${iconColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
            {icon}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-500 dark:text-indices-muted truncate">{label}</p>
          <p className="text-dial-lg font-display text-primary-600 dark:text-lume-green">{value}</p>
          {subValue && (
            <p className="text-xs text-gray-500 dark:text-indices-muted">{subValue}</p>
          )}
        </div>
        {trend && (
          <div
            className={`text-xs font-medium ${
              trend > 0
                ? 'text-status-success'
                : trend < 0
                ? 'text-status-error'
                : 'text-gray-500 dark:text-indices-muted'
            }`}
          >
            {trend > 0 ? '+' : ''}{trend}%
          </div>
        )}
      </div>
    </Card>
  )
}

/**
 * List card - card with list items
 */
export function ListCard({ title, action, children, className = '', emptyMessage }) {
  const hasChildren = Array.isArray(children) ? children.length > 0 : !!children

  return (
    <Card padding="none" className={className}>
      {title && (
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-dial-border">
          <h3 className="font-semibold text-gray-900 dark:text-indices-bright font-display">{title}</h3>
          {action}
        </div>
      )}
      {hasChildren ? (
        <div className="divide-y divide-gray-200 dark:divide-dial-border">{children}</div>
      ) : emptyMessage ? (
        <div className="p-8 text-center text-gray-500 dark:text-indices-muted">{emptyMessage}</div>
      ) : null}
    </Card>
  )
}

/**
 * List card item
 */
export function ListCardItem({
  children,
  onClick,
  className = '',
  showChevron = false,
}) {
  return (
    <div
      className={`flex items-center gap-3 p-4 ${
        onClick ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-dial-elevated transition-colors' : ''
      } ${className}`}
      onClick={onClick}
    >
      <div className="flex-1 min-w-0">{children}</div>
      {showChevron && (
        <svg
          className="w-5 h-5 text-gray-400 dark:text-indices-muted flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      )}
    </div>
  )
}

/**
 * Action card - for quick action buttons
 */
export function ActionCard({
  icon,
  label,
  onClick,
  bgColor = 'bg-primary-100 dark:bg-lume-teal/20',
  iconColor = 'text-primary-600 dark:text-lume-teal',
  className = '',
  disabled = false,
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        flex flex-col items-center gap-2 p-3 rounded-xl
        transition-all active:scale-95
        disabled:opacity-50 disabled:cursor-not-allowed
        hover:bg-gray-100 dark:hover:bg-dial-elevated
        ${className}
      `}
    >
      <div className={`w-12 h-12 ${bgColor} ${iconColor} rounded-xl flex items-center justify-center`}>
        {icon}
      </div>
      <span className="text-xs text-gray-600 dark:text-indices-medium font-medium text-center">{label}</span>
    </button>
  )
}

/**
 * Info card - for displaying information with optional icon
 */
export function InfoCard({
  icon,
  title,
  children,
  variant = 'info',
  className = '',
}) {
  const variantStyles = {
    info: {
      bg: 'bg-status-info/10',
      border: 'border-status-info/30',
      iconColor: 'text-status-info',
      titleColor: 'text-status-info',
    },
    success: {
      bg: 'bg-status-success/10',
      border: 'border-status-success/30',
      iconColor: 'text-status-success',
      titleColor: 'text-status-success',
    },
    warning: {
      bg: 'bg-status-warning/10',
      border: 'border-status-warning/30',
      iconColor: 'text-status-warning',
      titleColor: 'text-status-warning',
    },
    error: {
      bg: 'bg-status-error/10',
      border: 'border-status-error/30',
      iconColor: 'text-status-error',
      titleColor: 'text-status-error',
    },
  }

  const styles = variantStyles[variant]

  return (
    <div
      className={`${styles.bg} border ${styles.border} rounded-xl p-4 ${className}`}
    >
      <div className="flex gap-3">
        {icon && (
          <div className={`flex-shrink-0 ${styles.iconColor}`}>{icon}</div>
        )}
        <div className="flex-1">
          {title && (
            <h4 className={`font-medium ${styles.titleColor} mb-1`}>{title}</h4>
          )}
          <div className="text-sm text-gray-600 dark:text-indices-medium">{children}</div>
        </div>
      </div>
    </div>
  )
}

/**
 * Empty state card
 */
export function EmptyCard({
  icon,
  title,
  description,
  action,
  className = '',
}) {
  return (
    <Card className={`text-center py-8 ${className}`}>
      {icon && (
        <div className="mx-auto w-12 h-12 bg-gray-100 dark:bg-dial-elevated rounded-full flex items-center justify-center mb-4">
          <span className="text-gray-400 dark:text-indices-muted">{icon}</span>
        </div>
      )}
      <h3 className="text-lg font-medium text-gray-900 dark:text-indices-bright mb-1 font-display">{title}</h3>
      {description && (
        <p className="text-gray-500 dark:text-indices-muted mb-4">{description}</p>
      )}
      {action}
    </Card>
  )
}

export default {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  StatsCard,
  ListCard,
  ListCardItem,
  ActionCard,
  InfoCard,
  EmptyCard,
}
